import React, { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { isNil, isEmpty } from 'ramda'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { Typography } from '@material-ui/core'
import { withStyles, makeStyles } from '@material-ui/core/styles'
import {
  TWITTER_VALUE,
  FACEBOOK_VALUE,
  INSTAGRAM_VALUE,
  LINKEDIN_VALUE,
} from 'app/constants/channel'

import LightTooltip from 'app/componentV4/Tooltip/LightTooltip'

const CustomLightTooltip = withStyles({
  tooltip: {
    maxWidth: '250px',
  },
})(LightTooltip)

const useStyles = makeStyles({
  root: {
    cursor: 'pointer',
  },
  limitWidth: {
    maxWidth: '12vw',
  },
  truncate: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    width: '12vw',
  },
})

const Recipients = (props) => {
  const {
    groups,
    recipients,
    isTooltip = false,
    channels,
    isScheduled = false,
    isDraft = false,
    className = '',
  } = props

  const classes = useStyles()
  const { formatMessage } = useIntl()

  const content = useRef(null)
  const [subscribers, setSubscribers] = useState([])
  const [shouldTruncate, setShouldTruncate] = useState(false)
  const another = formatMessage({ id: 'text.another' })

  const handleEmptyRecipient = useCallback(() => {
    return (
      <Typography className="text-14 text-primary font-normal">-</Typography>
    )
  }, [formatMessage, isDraft, isScheduled])

  const handleRecipient = useCallback(
    (subscribers, inlineText, isAnother = false) => {
      if (isEmpty(subscribers)) return handleEmptyRecipient()

      return (
        <div className="space-x-2">
          {subscribers.map((subscriber, index) => (
            <Typography
              component="span"
              key={`tooltip-${subscriber.id}-${subscriber.name}`}
              className={clsx(
                inlineText,
                !isNil(subscriber.deletedAt) && 'line-through',
              )}
            >
              {subscriber.name}
              {index !== subscribers.length - 1 && ','}
            </Typography>
          ))}
        </div>
      )
    },
    [another, channels, handleEmptyRecipient],
  )

  useEffect(() => {
    let isMounted = true

    let recipientsName = []

    if (!isEmpty(recipients)) {
      recipientsName = recipients.map((recipient) => {
        return {
          ...recipient,
          name: `${recipient.firstName} ${recipient.lastName}`,
        }
      })
    }

    if (isMounted) {
      setSubscribers(groups.concat(recipientsName))
    }

    return () => {
      isMounted = false
    }
  }, [groups, recipients])

  useEffect(() => {
    const checkWidth = () => {
      if (content.current) {
        const width = content.current.clientWidth
        const vwWidth = window.innerWidth * 0.12
        setShouldTruncate(width > vwWidth)
      }
    }

    if (isTooltip) {
      checkWidth()
      window.addEventListener('resize', checkWidth)
    }

    return () => {
      window.removeEventListener('resize', checkWidth)
    }
  }, [isTooltip, subscribers])

  return (
    <>
      {isTooltip ? (
        <div className={clsx(className, classes.root)}>
          <CustomLightTooltip
            title={handleRecipient(subscribers, 'text-12 font-medium', true)}
            placement="bottom-start"
          >
            <div
              className={clsx(
                classes.limitWidth,
                'flex truncate items-baseline',
              )}
            >
              {isEmpty(subscribers) ? (
                handleEmptyRecipient()
              ) : (
                <div
                  ref={content}
                  className={clsx(
                    'overflow-hidden',
                    shouldTruncate && classes.truncate,
                  )}
                >
                  {subscribers.map((subscriber, index) => (
                    <Typography
                      key={`${subscriber.id}-${subscriber.name}-${index}`}
                      component="span"
                      className={clsx(
                        'recipient-tooltip',
                        'text-16 text-neutrals-600 font-normal mr-3',
                        !isNil(subscriber.deletedAt) && 'line-through',
                      )}
                    >
                      {subscriber.name}
                      {index !== subscribers.length - 1 && ','}
                    </Typography>
                  ))}
                </div>
              )}
            </div>
          </CustomLightTooltip>
        </div>
      ) : (
        handleRecipient(subscribers, 'text-14 font-bold')
      )}
    </>
  )
}

Recipients.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      groupId: PropTypes.number,
      groupType: PropTypes.number,
      groupName: PropTypes.string,
      tagId: PropTypes.number,
      name: PropTypes.string,
      deletedAt: PropTypes.string,
    }),
  ),
  recipients: PropTypes.arrayOf(
    PropTypes.shape({
      subscriberId: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      avatar: PropTypes.string,
      deletedAt: PropTypes.string,
    }),
  ),
  channels: PropTypes.arrayOf(PropTypes.number),
  isTooltip: PropTypes.bool,
  isScheduled: PropTypes.bool,
  isDraft: PropTypes.bool,
  className: PropTypes.string,
}

export default Recipients
